/* Spotlight Services — "Featured Presentations". Large cinema-card
   tiles with star ratings, "Now Showing" stamps, and "Reserve Seat"
   CTAs. Pure marquee energy. */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { tenantHref } from "@/lib/tenant";
import { EditorialSkeleton } from "../editorial/_EditorialSkeleton";

function priceFrom(svc) {
  const tiers = Array.isArray(svc?.tiers) ? svc.tiers : [];
  const tp = tiers.map(t => Number(t?.price)).filter(n => Number.isFinite(n) && n > 0);
  if (tp.length) return Math.min(...tp);
  const n = Number(svc?.price_from ?? svc?.starting_price ?? svc?.price);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export default function SpotlightServices() {
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const [services, setServices] = useState(null);
  useEffect(() => {
    api.get("/services").then(r => setServices(Array.isArray(r.data) ? r.data : [])).catch(() => setServices([]));
    document.title = `${studioName} — Now Showing`;
  }, [studioName]);

  if (services === null) return <EditorialSkeleton eyebrow="Curtain up." />;

  return (
    <div data-testid="spotlight-services" style={{ background: "var(--tenant-bg, #0A0A0F)", color: "var(--tenant-fg, #F5E9C9)" }}>

      <section className="px-6 lg:px-16 pt-20 pb-12 text-center border-b" style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 35%, transparent)" }}>
        <div className="text-[10px] uppercase tracking-[0.5em] mb-5" style={{ color: "var(--tenant-accent)" }}>
          ✦ Now Showing
        </div>
        <h1 className="uppercase leading-[0.95]" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(2.5rem, 7vw, 5rem)" }}>
          Featured presentations.
        </h1>
      </section>

      <div className="max-w-[1280px] mx-auto px-6 lg:px-16 py-16 grid grid-cols-1 md:grid-cols-2 gap-px"
           style={{ background: "color-mix(in srgb, var(--tenant-accent) 35%, transparent)" }}>
        {services.map((svc, i) => {
          const p = priceFrom(svc);
          return (
            <article key={svc.id} id={svc.id || svc.slug}
                     className="p-10 text-center" style={{ background: "var(--tenant-bg)" }}>
              <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.5em] mb-6">
                <span style={{ color: "var(--tenant-accent)" }}>✦ {String(i + 1).padStart(2, "0")}</span>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className="w-3 h-3 fill-current" style={{ color: "var(--tenant-accent)" }} strokeWidth={0} />
                  ))}
                </div>
              </div>
              <h2 className="text-3xl uppercase mb-4 leading-tight" style={{ fontFamily: "var(--tenant-serif)" }}>
                {svc.name}
              </h2>
              {(svc.description || svc.short_description) && (
                <p className="text-sm leading-relaxed mb-6 max-w-md mx-auto" style={{ color: "color-mix(in srgb, var(--tenant-fg) 70%, transparent)" }}>
                  {svc.description || svc.short_description}
                </p>
              )}
              {p !== null && (
                <div className="text-[10px] uppercase tracking-[0.42em] mb-6" style={{ color: "var(--tenant-fg-soft)" }}>
                  Tickets from <span style={{ color: "var(--tenant-accent)" }}>${p.toFixed(0)}</span>
                </div>
              )}
              <Link to={tenantHref("/booking")}
                className="inline-flex items-center gap-3 px-7 py-3 text-[10px] uppercase tracking-[0.5em]"
                style={{ background: "var(--tenant-accent)", color: "var(--tenant-bg)" }}>
                ✦ Reserve seat
              </Link>
            </article>
          );
        })}
        {services.length === 0 && (
          <p className="col-span-2 p-12 text-center text-[10px] uppercase tracking-[0.5em]" style={{ background: "var(--tenant-bg)", color: "var(--tenant-fg-soft)" }}>
            ✦ Coming soon
          </p>
        )}
      </div>
    </div>
  );
}
